class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.isMegaMenu = this.mainDetailsToggle.classList.contains('mega-menu');
    this.openTimer = null;
    this.closeTimer = null;
    this.closeAnimationTimer = null;
    this.intentTimer = null;
    this.openAnimationFrame = null;
    this.lastX = 0;
    this.lastY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.HOVER_DELAY = 300;
    this.CLOSE_DELAY = 400;
    this.EXIT_DURATION = 280;
    this.INTENT_SENSITIVITY = 7;
    this.INTENT_INTERVAL = 100;
    this.canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    this.canFinePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;

    if (this.isMegaMenu) HeaderMenu.ensureOverlay();
    HeaderMenu.bindViewportListeners();

    if (this.canHover) {
      this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
      this.addEventListener('mousemove', this.onMouseMove.bind(this));
      this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    }

    if (this.summary && this.summary.matches('[data-mega-menu-summary]')) {
      this.summary.addEventListener('click', this.onSummaryClick.bind(this));
      this.summary.addEventListener('auxclick', this.onSummaryAuxClick.bind(this));
    }

    this.addEventListener('keyup', this.onKeyUp.bind(this), true);
  }

  static ensureOverlay() {
    let overlay = document.querySelector('.mega-menu__overlay');

    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'mega-menu__overlay';
      overlay.setAttribute('aria-hidden', 'true');
      document.body.appendChild(overlay);
    }

    if (!overlay.dataset.megaMenuBound) {
      overlay.addEventListener('click', () => HeaderMenu.closeAll());
      overlay.dataset.megaMenuBound = 'true';
    }
  }

  static bindViewportListeners() {
    if (HeaderMenu.viewportListenersBound) return;

    const syncOpenMenus = () => {
      document.querySelectorAll('header-menu').forEach((menu) => {
        if (typeof menu.updateHeaderBottomPosition === 'function') menu.updateHeaderBottomPosition();
        if (typeof menu.syncHeaderState === 'function') menu.syncHeaderState();
      });
    };

    window.addEventListener('resize', syncOpenMenus);
    window.addEventListener('scroll', syncOpenMenus, { passive: true });
    HeaderMenu.viewportListenersBound = true;
  }

  static closeAll(options = {}) {
    document.querySelectorAll('header-menu').forEach((menu) => {
      if (typeof menu.closeMenu === 'function') menu.closeMenu(options);
    });
  }

  onToggle() {
    this.syncHeaderState();
    this.updateHeaderBottomPosition();

    if (this.mainDetailsToggle.open) {
      this.openMenu({ setOpen: false });
    } else {
      this.closeMenu({ immediate: true, preserveFocus: true });
    }
  }

  onMouseMove(e) {
    this.currentX = e.clientX;
    this.currentY = e.clientY;
  }

  onMouseEnter(e) {
    document.querySelectorAll('header-menu').forEach((hm) => {
      if (typeof hm.cancelClose === 'function') hm.cancelClose();
    });

    if (this.mainDetailsToggle.open) {
      this.openMenu({ setOpen: false });
      return;
    }

    this.cancelOpenIntent();
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.currentX = e.clientX;
    this.currentY = e.clientY;

    const anyOpen = document.querySelector('header-menu details[open], header-menu details.is-active');

    if (anyOpen) {
      this.openMenu();
    } else {
      this.intentTimer = setInterval(() => {
        const dx = this.currentX - this.lastX;
        const dy = this.currentY - this.lastY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < this.INTENT_SENSITIVITY) {
          clearInterval(this.intentTimer);
          this.openTimer = setTimeout(() => this.openMenu(), 0);
        } else {
          this.lastX = this.currentX;
          this.lastY = this.currentY;
        }
      }, this.INTENT_INTERVAL);

      this.openTimer = setTimeout(() => {
        clearInterval(this.intentTimer);
        this.openMenu();
      }, this.HOVER_DELAY);
    }
  }

  onSummaryClick(event) {
    const href = this.summary.dataset.href;
    if (!href || href === '#') return;

    if (!this.shouldNavigateFromSummaryClick(event)) return;

    event.preventDefault();
    this.summary.setAttribute('aria-expanded', this.mainDetailsToggle.open);

    if (event.metaKey || event.ctrlKey || event.shiftKey) {
      window.open(href, '_blank');
    } else {
      window.location.href = href;
    }
  }

  onSummaryAuxClick(event) {
    const href = this.summary.dataset.href;
    if (!href || href === '#' || event.button !== 1 || !this.shouldNavigateFromSummaryClick(event)) return;

    event.preventDefault();
    window.open(href, '_blank');
    this.summary.setAttribute('aria-expanded', this.mainDetailsToggle.open);
  }

  shouldNavigateFromSummaryClick(event) {
    return this.canHover && this.canFinePointer && event.detail > 0;
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE' || !this.mainDetailsToggle.open) return;

    event.preventDefault();
    event.stopPropagation();
    this.closeMenu({ immediate: true, focusSummary: true });
  }

  openMenu(options = {}) {
    const { setOpen = true } = options;

    this.cancelClose();
    this.cancelOpenIntent();
    this.closeOtherMenus();

    if (setOpen && !this.mainDetailsToggle.open) {
      this.mainDetailsToggle.setAttribute('open', '');
    }

    this.summary.setAttribute('aria-expanded', true);
    this.updateHeaderBottomPosition();

    if (this.isMegaMenu) document.body.classList.add('mega-menu-is-open');

    this.cancelOpenAnimationFrame();
    this.openAnimationFrame = requestAnimationFrame(() => {
      this.openAnimationFrame = requestAnimationFrame(() => {
        this.mainDetailsToggle.classList.add('is-active');
        this.openAnimationFrame = null;
      });
    });

    this.syncHeaderState();
  }

  closeMenu(options = {}) {
    const { immediate = false, focusSummary = false, preserveFocus = false } = options;

    this.cancelOpenIntent();
    this.cancelOpenAnimationFrame();
    this.cancelClose();

    this.mainDetailsToggle.classList.remove('is-active');
    this.summary.setAttribute('aria-expanded', false);

    if (this.isMegaMenu && !this.hasOtherActiveMegaMenu()) {
      document.body.classList.remove('mega-menu-is-open');
    }

    const finishClose = () => {
      this.mainDetailsToggle.removeAttribute('open');
      this.syncHeaderState();

      if (focusSummary) {
        this.summary.focus();
      } else if (!preserveFocus && this.contains(document.activeElement)) {
        this.summary.focus();
      }
    };

    if (immediate) {
      finishClose();
    } else {
      this.closeAnimationTimer = setTimeout(() => {
        this.closeAnimationTimer = null;
        finishClose();
      }, this.EXIT_DURATION);
    }
  }

  closeOtherMenus() {
    document.querySelectorAll('header-menu').forEach((menu) => {
      if (menu !== this && typeof menu.closeMenu === 'function') {
        menu.closeMenu({ immediate: true, preserveFocus: true });
      }
    });
  }

  onMouseLeave() {
    this.cancelOpenIntent();
    this.cancelClose();
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.closeMenu();
    }, this.CLOSE_DELAY);
  }

  close() {
    this.closeMenu({ immediate: true, preserveFocus: true });
  }

  cancelOpenIntent() {
    clearTimeout(this.openTimer);
    clearInterval(this.intentTimer);
    this.openTimer = null;
    this.intentTimer = null;
  }

  cancelClose() {
    clearTimeout(this.closeTimer);
    clearTimeout(this.closeAnimationTimer);
    this.closeTimer = null;
    this.closeAnimationTimer = null;
  }

  cancelOpenAnimationFrame() {
    if (!this.openAnimationFrame) return;
    cancelAnimationFrame(this.openAnimationFrame);
    this.openAnimationFrame = null;
  }

  hasOtherActiveMegaMenu() {
    return Array.from(document.querySelectorAll('header-menu')).some((menu) => {
      return (
        menu !== this &&
        menu.mainDetailsToggle &&
        menu.mainDetailsToggle.classList.contains('mega-menu') &&
        (menu.mainDetailsToggle.open || menu.mainDetailsToggle.classList.contains('is-active'))
      );
    });
  }

  syncHeaderState() {
    if (!this.header) return;
    this.header.preventHide = Boolean(document.querySelector('header-menu details[open], header-menu details.is-active'));
  }

  updateHeaderBottomPosition() {
    if (!this.header || !this.mainDetailsToggle.open) return;

    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
